import type { PointLike } from 'react-map-gl/mapbox';
import {
  COLOR_DATAVIZ_PUBLIC_LANDS_BLM,
  COLOR_DATAVIZ_PUBLIC_LANDS_CITY_LAND,
  COLOR_DATAVIZ_PUBLIC_LANDS_COUNTY_AGENCY,
  COLOR_DATAVIZ_PUBLIC_LANDS_DOD,
  COLOR_DATAVIZ_PUBLIC_LANDS_FWS,
  COLOR_DATAVIZ_PUBLIC_LANDS_NATIVE_AMERICAN_ALASKA_NATIVE_LAND,
  COLOR_DATAVIZ_PUBLIC_LANDS_NGO,
  COLOR_DATAVIZ_PUBLIC_LANDS_NPS,
  COLOR_DATAVIZ_PUBLIC_LANDS_REGIONAL_AGENCY,
  COLOR_DATAVIZ_PUBLIC_LANDS_STATE_DNR,
  COLOR_DATAVIZ_PUBLIC_LANDS_STATE_PARKS_AND_REC,
  COLOR_DATAVIZ_PUBLIC_LANDS_USACE,
  COLOR_DATAVIZ_PUBLIC_LANDS_USBR,
  COLOR_DATAVIZ_PUBLIC_LANDS_USFS,
  COLOR_DATAVIZ_SLOPE_1,
  COLOR_DATAVIZ_SLOPE_2,
  COLOR_DATAVIZ_SLOPE_3,
  COLOR_DATAVIZ_SLOPE_4,
  COLOR_DATAVIZ_SLOPE_5,
  COLOR_DATAVIZ_SLOPE_6,
  COLOR_DATAVIZ_SLOPE_7,
  COLOR_DATAVIZ_SLOPE_8
} from '@alltrails/denali/tokens';

export const propertyLabels: Record<string, string> = {
  mang_name: 'Manager',
  pub_access: 'Access',
  des_tp: 'Designation',
  gis_acres: 'Acres',
  unit_nm: 'Unit',
  loc_nm: 'Location',
  state_nm: 'State'
};

export type ManagerCode = 'BLM' | 'USFS' | 'FWS' | 'NPS' | 'DOD' | 'USACE' | 'USBR' | 'SPR' | 'SDNR' | 'REG' | 'CNTY' | 'CITY' | 'TRIB' | 'NGO';
export const defaultManagerCodes: ManagerCode[] = [
  'BLM',
  'USFS',
  'FWS',
  'NPS',
  'DOD',
  'USACE',
  'USBR',
  'SPR',
  'SDNR',
  'REG',
  'CNTY',
  'CITY',
  'TRIB',
  'NGO'
];
// Source for manager names and designation types: https://docs.google.com/spreadsheets/d/1BNSdrX8LTcn0VkDJsZcMl1MTcfnvpl9lwpQZai_hWc4/edit?gid=1407104117#gid=1407104117
export const managerNames: Record<ManagerCode, string> = {
  BLM: 'Bureau of Land Management (BLM)',
  USFS: 'U.S. Forest Service (USFS)',
  FWS: 'Fish and Wildlife Service (FWS)',
  NPS: 'National Park Service (NPS)',
  DOD: 'Department of Defense (DOD)',
  USACE: 'U.S. Army Corps of Engineers (USACE)',
  USBR: 'U.S. Bureau of Reclamation (USBR)',
  SPR: 'State parks and recreation agency',
  SDNR: 'State department of natural resources',
  REG: 'Regional agency',
  CNTY: 'County agency',
  CITY: 'City land',
  TRIB: 'American Indian/Alaska Native land',
  NGO: 'Non-governmental organization (NGO)'
};

export const designationTypes: Record<string, string> = {
  NP: 'National park',
  NM: 'National monument',
  NCA: 'Conservation area',
  NF: 'National forest',
  NG: 'National grassland',
  PUB: 'National public lands',
  NT: 'National scenic or historic trail',
  NWR: 'National wildlife refuge',
  WA: 'Wilderness area',
  WSR: 'Wild and scenic river',
  WSA: 'Wilderness study area',
  MPA: 'Marine protected area',
  NRA: 'National recreation area',
  NSBV: 'National scenic, botanical, or volcanic area',
  NLS: 'National lakeshore or seashore',
  IRA: 'Inventoried roadless area',
  ACEC: 'Area of critical environmental concern',
  RNA: 'Research natural area',
  REC: 'Recreation management area',
  RMA: 'Resource management area',
  WPA: 'Watershed protection area',
  REA: 'Research or educational area',
  HCA: 'Historic or cultural area',
  MIT: 'Mitigation land or bank',
  MIL: 'Military land',
  ACC: 'Access area',
  SDA: 'Special designation area',
  PROC: 'Approved or proclamation boundary',
  FOTH: 'Federal other or unknown',
  ND: 'Not designated',
  TRIBL: 'Native American land area',
  OCS: 'Outer Continental Shelf area',
  SP: 'State park',
  SW: 'State wilderness',
  SCA: 'State conservation area',
  SREC: 'State recreation area',
  SHCA: 'State historic or cultural area',
  SRMA: 'State resource management area',
  SOTH: 'State other or unknown',
  LP: 'Local park',
  LCA: 'Local conservation area',
  LREC: 'Local recreation area',
  LHCA: 'Local historic or cultural area',
  LRMA: 'Local resource management area',
  LOTH: 'Local other or unknown',
  PCON: 'Private conservation',
  PPRK: 'Private park',
  PREC: 'Private recreation or educational area',
  PHCA: 'Private historic or cultural area',
  PAGR: 'Private agricultural area',
  PRAN: 'Private ranch',
  PFOR: 'Private forest stewardship area',
  POTH: 'Private other or unknown',
  CONE: 'Conservation easement',
  RECE: 'Recreation or educational easement',
  HCAE: 'Historic or cultural easement',
  AGRE: 'Agricultural easement',
  RANE: 'Ranch easement',
  FORE: 'Forest stewardship easement',
  OTHE: 'Other easement',
  UNKE: 'Unknown easement',
  UNK: 'Unknown'
};

export const publicAccessTypes: Record<string, string> = {
  OA: 'Open access',
  RA: 'Restricted access',
  XA: 'Closed',
  UK: 'Unknown'
};

export type LegendItem = {
  color: string;
  code: ManagerCode;
};

export const legendItems: LegendItem[] = [
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_BLM, code: 'BLM' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_USFS, code: 'USFS' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_FWS, code: 'FWS' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_NPS, code: 'NPS' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_DOD, code: 'DOD' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_USACE, code: 'USACE' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_USBR, code: 'USBR' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_STATE_PARKS_AND_REC, code: 'SPR' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_STATE_DNR, code: 'SDNR' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_REGIONAL_AGENCY, code: 'REG' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_COUNTY_AGENCY, code: 'CNTY' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_CITY_LAND, code: 'CITY' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_NATIVE_AMERICAN_ALASKA_NATIVE_LAND, code: 'TRIB' },
  { color: COLOR_DATAVIZ_PUBLIC_LANDS_NGO, code: 'NGO' }
];

export const formatPropertyValue = (key: string, value: string | number | null | undefined): string => {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  // Format acres with commas
  if (key === 'gis_acres' && typeof value === 'number') {
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  // Map manager names to full names
  if (key === 'mang_name' && typeof value === 'string') {
    return managerNames[value] || value;
  }

  // Map designation types to full names
  if (key === 'des_tp' && typeof value === 'string') {
    return designationTypes[value] || value;
  }

  // Map public access types to full names
  if (key === 'pub_access' && typeof value === 'string') {
    return publicAccessTypes[value] || value;
  }

  return String(value);
};

// offsetting overlay popups (public lands, wildfire, etc.) when a feature is hovered/clicked:
export const popupOffset: Record<string, PointLike> = {
  center: [0, 0],
  left: [64, 17],
  right: [-64, 17],
  top: [0, 10],
  bottom: [0, -49],
  'top-right': [-64, -42],
  'top-left': [64, -42],
  'bottom-left': [64, 2],
  'bottom-right': [-64, 2]
};

export const slopeAngleLegendItems = [
  {
    color: COLOR_DATAVIZ_SLOPE_1,
    code: '21°'
  },
  {
    color: COLOR_DATAVIZ_SLOPE_2,
    code: '24°'
  },
  {
    color: COLOR_DATAVIZ_SLOPE_3,
    code: '28°'
  },
  {
    color: COLOR_DATAVIZ_SLOPE_4,
    code: '30°'
  },
  {
    color: COLOR_DATAVIZ_SLOPE_5,
    code: '35°'
  },
  {
    color: COLOR_DATAVIZ_SLOPE_6,
    code: '41°'
  },
  {
    color: COLOR_DATAVIZ_SLOPE_7,
    code: '51°'
  },
  {
    color: COLOR_DATAVIZ_SLOPE_8,
    code: '61°'
  }
];
